export interface DriftState {
  offsetX: number;
  offsetY: number;
}

export interface DriftConfig {
  /** px shifted per accepted observation. */
  step: number;
  /** px, per-axis clamp on the accumulated offset. */
  max: number;
  /** Minimum `confidence` (the drift judgment's own) to accept an observation. */
  minConfidence: number;
}

export const DRIFT_DEFAULTS: DriftConfig = {
  step: 0.03,
  max: 0.25,
  minConfidence: 0.6,
};

/** Reading reliability floor below which a drift observation is ignored regardless of
 * the drift judgment's own confidence — an unreliable eye-tracking read can't be
 * trusted to tell calibration bias apart from noise. */
const RELIABLE_FLOOR = 0.6;

function clamp(v: number, max: number): number {
  return Math.max(-max, Math.min(max, v));
}

/**
 * Folds one `calibrationDrift` observation into `state`. Ignores `direction === "none"`,
 * `confidence < cfg.minConfidence`, or `reliable < 0.6` (the observation itself is
 * untrustworthy) — returning `state` unchanged. Otherwise shifts the neutral-point
 * offset by `cfg.step` *against* the reported bias so the calibration mapping pushes
 * back the other way (a cursor reported sitting left of the true gaze needs the
 * mapping to push right for the same raw gaze, which — since `applyCalibration`
 * subtracts `neutral` before scaling — means lowering `neutral.x`; see
 * `client/game.ts`'s wiring, which adds this state onto `calibration.neutral`).
 * Each axis is clamped to `+/- cfg.max`. Pure.
 */
export function applyDriftObservation(
  state: DriftState,
  direction: "none" | "left" | "right" | "up" | "down",
  confidence: number,
  reliable: number,
  cfg: Partial<DriftConfig> = {},
): DriftState {
  const config = { ...DRIFT_DEFAULTS, ...cfg };

  if (direction === "none" || confidence < config.minConfidence || reliable < RELIABLE_FLOOR) {
    return state;
  }

  switch (direction) {
    case "left":
      return { offsetX: clamp(state.offsetX - config.step, config.max), offsetY: state.offsetY };
    case "right":
      return { offsetX: clamp(state.offsetX + config.step, config.max), offsetY: state.offsetY };
    case "up":
      return { offsetX: state.offsetX, offsetY: clamp(state.offsetY - config.step, config.max) };
    case "down":
      return { offsetX: state.offsetX, offsetY: clamp(state.offsetY + config.step, config.max) };
  }
}
